"use client";

import { createContext, PropsWithChildren, useContext, useRef } from "react";
import { AlgoliaSearchClientConfig } from "../_search-providers/types";
import { AlgoliaInsights } from "./algolia-insights";
import { HubspotTracking } from "./hubspot-tracking";

interface ContextState {
	insightsClient: AlgoliaInsights;
}

interface ProviderProps extends PropsWithChildren {
	algoliaConfig: AlgoliaSearchClientConfig;
}

export const AnalyticsContext = createContext({} as ContextState);

export const AnalyticsContextProvider = ({
	algoliaConfig,
	children,
}: ProviderProps) => {
	const insightsClientRef = useRef<AlgoliaInsights>(null);
	const hubspotClientRef = useRef<HubspotTracking>(null);

	// Lazy initialization
	if (insightsClientRef.current === null) {
		insightsClientRef.current = new AlgoliaInsights(algoliaConfig);
		hubspotClientRef.current = new HubspotTracking();
	}

	if (hubspotClientRef.current === null) {
		hubspotClientRef.current = new HubspotTracking();
	}

	const value = {
		insightsClient: insightsClientRef.current,
	} as ContextState;

	return (
		<AnalyticsContext.Provider value={value}>
			{children}
		</AnalyticsContext.Provider>
	);
};

export const useAnalyticsContext = () => {
	const context = useContext(AnalyticsContext);

	if (!context) {
		throw new Error(
			"useAnalyticsContext must be used within an AnalyticsContextProvider",
		);
	}

	return context;
};
